import { Link, useNavigate, useLocation } from "react-router-dom";

const footerLinks = [
  {
    title: "Nos domaines",
    links: [
      { label: "Toitures-terrasses", href: "/services/toitures-terrasses", type: "route" },
      { label: "Parkings (enrobés & asphalte)", href: "/services/parkings", type: "route" },
      { label: "Dalles carrossables", href: "/services/dalles-carrossables", type: "route" },
      { label: "Terrasses", href: "/services/terrasses", type: "route" },
      { label: "Étanchéité + isolation thermique", href: "/services/etancheite-isolation", type: "route" },
    ],
  },
  {
    title: "Techniques",
    links: [
      { label: "Membranes bitumineuses", href: "/services/membranes-bitumineuses", type: "route" },
      { label: "Étanchéité liquide", href: "/services/etancheite-liquide", type: "route" },
      { label: "Géomembranes EPDM", href: "/services/geomembranes-epdm", type: "route" },
      { label: "Réfection étanchéité", href: "/services/refection-etancheite", type: "route" },
    ],
  },
  {
    title: "Entreprise",
    links: [
      { label: "À propos de KS BTP", href: "#apropos", type: "anchor" },
      { label: "Notre approche", href: "/notre-approche", type: "route" },
      { label: "Nos réalisations", href: "#realisations", type: "anchor" },
      { label: "Blog & Conseils", href: "/blog", type: "route" },
      { label: "Demander un devis", href: "/devis", type: "route" },
      { label: "Contact", href: "/contact", type: "route" },
    ],
  },
];

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleAnchor = (e: React.MouseEvent<HTMLAnchorElement>, anchor: string) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.querySelector(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(anchor);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 350);
    }
  };

  return (
    <footer className="bg-[#0d2b18] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
              Votre partenaire de confiance pour tous vos projets d&apos;étanchéité. Des matériaux adaptés aux contraintes climatiques, un savoir-faire reconnu et un engagement total envers la qualité.
            </p>
            <div className="flex flex-col gap-2 mb-6">
              <a href="tel:0175434346" className="flex items-center gap-2 text-gray-400 text-sm hover:text-[#4cd97a] transition-colors cursor-pointer">
                <i className="ri-phone-line text-[#00a340]"></i>
                01 75 43 43 46
              </a>
              <a href="mailto:contact@ksbtp.fr" className="flex items-center gap-2 text-gray-400 text-sm hover:text-[#4cd97a] transition-colors cursor-pointer">
                <i className="ri-mail-line text-[#00a340]"></i>
                contact@ksbtp.fr
              </a>
              <a href="https://maps.google.com/?q=9+rue+de+Lamirault+77090+Collegien" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 text-sm hover:text-[#4cd97a] transition-colors cursor-pointer">
                <i className="ri-map-pin-line text-[#00a340]"></i>
                9 rue de Lamirault, 77090 Collégien
              </a>
            </div>
            <div className="flex gap-3">
              {[
                { icon: "ri-linkedin-fill", href: "https://linkedin.com" },
                { icon: "ri-twitter-x-line", href: "https://twitter.com" },
                { icon: "ri-instagram-line", href: "https://instagram.com" },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-600 text-gray-400 hover:border-[#00a340] hover:text-[#00a340] transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {footerLinks.map((col) => (
            <div key={col.title}>
              <h4 className="font-bold text-white text-sm mb-4 uppercase tracking-wider">{col.title}</h4>
              <ul className="flex flex-col gap-2.5">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.type === "route" ? (
                      <Link
                        to={link.href}
                        className="text-gray-400 text-sm hover:text-[#4cd97a] transition-colors cursor-pointer"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        onClick={(e) => handleAnchor(e, link.href)}
                        className="text-gray-400 text-sm hover:text-[#4cd97a] transition-colors cursor-pointer"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-gray-700/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-xs">
            © 2026 KS BTP — Spécialiste en Étanchéité. Tous droits réservés.
          </p>
          <div className="flex gap-5">
            <a href="#" rel="nofollow" className="text-gray-500 text-xs hover:text-gray-300 transition-colors cursor-pointer">
              Politique de confidentialité
            </a>
            <Link to="/mentions-legales" className="text-gray-500 text-xs hover:text-gray-300 transition-colors cursor-pointer">
              Mentions légales
            </Link>
            <a href="#" rel="nofollow" className="text-gray-500 text-xs hover:text-gray-300 transition-colors cursor-pointer">
              Plan du site
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
